import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  PenTool,
  CheckCircle2,
  Palette,
  Type,
  Send,
  User,
  Clock,
  RefreshCw,
} from 'lucide-react'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  dateColumn,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface PensakiFont {
  id: string
  name: string
  preview?: string
  category?: string
}

interface PensakiColor {
  id: string
  name: string
  hex?: string
}

interface OrderResult {
  orderId: string
  status: string
  estimatedDelivery?: string
  price: number
  currency: string
}

interface OrderHistoryItem {
  orderId: string
  status: string
  estimatedDelivery?: string
  price: number
  currency: string
}

type Tab = 'compose' | 'history'

export function PensakiPage() {
  const [tab, setTab] = useState<Tab>('compose')
  const [step, setStep] = useState<'compose' | 'preview' | 'sent'>('compose')
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null)
  const { toast } = useToast()

  const [recipient, setRecipient] = useState({
    name: '',
    company: '',
    street: '',
    zipCode: '',
    city: '',
    country: 'DE',
  })

  const [sender, setSender] = useState({
    name: '',
    company: '',
    street: '',
    zipCode: '',
    city: '',
  })

  const [card, setCard] = useState({
    type: 'letter' as string,
    message: '',
    handwritingStyle: '',
    inkColor: '',
  })

  const fontsQuery = useQuery<PensakiFont[]>({
    queryKey: ['pensaki-fonts'],
    queryFn: () => api.get('/shipping/pensaki/fonts').then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const colorsQuery = useQuery<PensakiColor[]>({
    queryKey: ['pensaki-colors'],
    queryFn: () => api.get('/shipping/pensaki/colors').then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const historyQuery = useQuery<{ orders: OrderHistoryItem[]; total: number }>({
    queryKey: ['pensaki-history'],
    queryFn: () => api.get('/shipping/pensaki/orders').then((r) => r.data),
    enabled: tab === 'history',
    retry: false,
  })

  const sendMutation = useMutation({
    mutationFn: (data: unknown) => api.post('/shipping/pensaki/send', data).then((r) => r.data),
    onSuccess: (data: OrderResult) => {
      setOrderResult(data)
      setStep('sent')
      toast({ title: 'Auftrag erstellt', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Erstellen des Auftrags', tone: 'danger' })
    },
  })

  const hasSender = sender.name && sender.street && sender.zipCode && sender.city

  function handlePreview() {
    setStep('preview')
  }

  function handleSend() {
    sendMutation.mutate({
      recipient: {
        ...recipient,
        company: recipient.company || undefined,
      },
      sender: hasSender
        ? {
            ...sender,
            company: sender.company || undefined,
          }
        : undefined,
      card: {
        ...card,
        handwritingStyle: card.handwritingStyle || undefined,
        inkColor: card.inkColor || undefined,
      },
    })
  }

  function resetForm() {
    setStep('compose')
    setOrderResult(null)
    setCard({ type: 'letter', message: '', handwritingStyle: '', inkColor: '' })
    setRecipient({ name: '', company: '', street: '', zipCode: '', city: '', country: 'DE' })
    setSender({ name: '', company: '', street: '', zipCode: '', city: '' })
  }

  const selectedFont = fontsQuery.data?.find((f) => f.id === card.handwritingStyle)
  const selectedColor = colorsQuery.data?.find((c) => c.id === card.inkColor)

  const historyColumns: ColumnDef<OrderHistoryItem>[] = [
    {
      accessorKey: 'orderId',
      header: 'Auftrags-ID',
      cell: ({ row }) => (
        <span className="font-mono text-caption">{row.original.orderId}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'price',
      header: 'Preis',
      cell: ({ row }) =>
        `${row.original.price.toLocaleString('de', { minimumFractionDigits: 2 })} ${row.original.currency}`,
    },
    dateColumn<OrderHistoryItem>('estimatedDelivery', 'Zustellung'),
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="PENSAKI Handgeschriebene Briefe"
        description="Einzelbriefe und Postkarten in echter Handschrift versenden"
        breadcrumbs={[{ label: 'Versand', to: '/versand' }, { label: 'PENSAKI' }]}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="compose">
            <PenTool className="w-4 h-4" />
            Brief erstellen
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="w-4 h-4" />
            Auftragshistorie
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose">
          {step === 'compose' && (
            <div className="space-y-6">
              <Card className="border-blue-200 bg-blue-50">
                <Card.Body className="text-small text-blue-800">
                  <p className="font-medium mb-1">Einzelbrief-Versand</p>
                  <p>
                    Einzelne handgeschriebene Briefe koennen direkt versendet werden. Bei
                    Standardformaten gilt eine Mindestbestellmenge von 5 Stueck – einzelne Briefe
                    werden zum Preis der Mindestmenge berechnet. Der genaue Preis wird in der
                    Vorschau angezeigt.
                  </p>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Empfaenger
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Name"
                      required
                      value={recipient.name}
                      onChange={(e) => setRecipient((r) => ({ ...r, name: e.target.value }))}
                    />
                    <Input
                      label="Firma (optional)"
                      value={recipient.company}
                      onChange={(e) => setRecipient((r) => ({ ...r, company: e.target.value }))}
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Strasse + Hausnr."
                        required
                        value={recipient.street}
                        onChange={(e) => setRecipient((r) => ({ ...r, street: e.target.value }))}
                      />
                    </div>
                    <Input
                      label="PLZ"
                      required
                      value={recipient.zipCode}
                      onChange={(e) => setRecipient((r) => ({ ...r, zipCode: e.target.value }))}
                    />
                    <Input
                      label="Stadt"
                      required
                      value={recipient.city}
                      onChange={(e) => setRecipient((r) => ({ ...r, city: e.target.value }))}
                    />
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Absender (optional)
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <p className="text-caption text-gray-500 mb-3">
                    Wird auf dem Brief als Absender gedruckt. Leer lassen fuer Standard-Absender.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Name"
                      value={sender.name}
                      onChange={(e) => setSender((s) => ({ ...s, name: e.target.value }))}
                    />
                    <Input
                      label="Firma (optional)"
                      value={sender.company}
                      onChange={(e) => setSender((s) => ({ ...s, company: e.target.value }))}
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Strasse + Hausnr."
                        value={sender.street}
                        onChange={(e) => setSender((s) => ({ ...s, street: e.target.value }))}
                      />
                    </div>
                    <Input
                      label="PLZ"
                      value={sender.zipCode}
                      onChange={(e) => setSender((s) => ({ ...s, zipCode: e.target.value }))}
                    />
                    <Input
                      label="Stadt"
                      value={sender.city}
                      onChange={(e) => setSender((s) => ({ ...s, city: e.target.value }))}
                    />
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Kartentyp</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="flex gap-3 flex-wrap">
                    {[
                      { value: 'letter', label: 'Brief' },
                      { value: 'postcard', label: 'Postkarte' },
                      { value: 'greeting_card', label: 'Grusskarte' },
                    ].map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={card.type === opt.value ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setCard((c) => ({ ...c, type: opt.value }))}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Nachricht
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <Textarea
                    required
                    rows={8}
                    value={card.message}
                    onChange={(e) => setCard((c) => ({ ...c, message: e.target.value }))}
                    placeholder="Ihre handgeschriebene Nachricht..."
                    hint={`${card.message.length} Zeichen`}
                  />
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Handschrift
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {fontsQuery.isLoading && <LoadingState label="Schriften werden geladen…" size="sm" />}
                  {fontsQuery.isError && (
                    <ErrorState
                      title="Schriften nicht ladbar"
                      description="PENSAKI nicht konfiguriert?"
                      details={(fontsQuery.error as Error)?.message}
                      size="sm"
                    />
                  )}
                  {fontsQuery.data && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {fontsQuery.data.map((font) => (
                        <button
                          key={font.id}
                          type="button"
                          onClick={() => setCard((c) => ({ ...c, handwritingStyle: font.id }))}
                          className={cn(
                            'p-3 rounded-md border text-left transition-colors',
                            card.handwritingStyle === font.id
                              ? 'border-brand-600 bg-brand-50'
                              : 'border-gray-200 hover:border-gray-300',
                          )}
                        >
                          <p className="text-small font-medium text-gray-800">{font.name}</p>
                          {font.category && (
                            <p className="text-caption text-gray-500 mt-0.5">{font.category}</p>
                          )}
                          {font.preview && (
                            <img
                              src={font.preview}
                              alt={font.name}
                              className="mt-2 h-8 object-contain"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Tintenfarbe
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {colorsQuery.isLoading && <LoadingState label="Farben werden geladen…" size="sm" />}
                  {colorsQuery.isError && (
                    <ErrorState
                      title="Farben nicht ladbar"
                      details={(colorsQuery.error as Error)?.message}
                      size="sm"
                    />
                  )}
                  {colorsQuery.data && (
                    <div className="flex gap-3 flex-wrap">
                      {colorsQuery.data.map((color) => (
                        <button
                          key={color.id}
                          type="button"
                          onClick={() => setCard((c) => ({ ...c, inkColor: color.id }))}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-md border transition-colors',
                            card.inkColor === color.id
                              ? 'border-brand-600 bg-brand-50'
                              : 'border-gray-200 hover:border-gray-300',
                          )}
                        >
                          <span
                            className="w-4 h-4 rounded-full border border-gray-300"
                            style={{ backgroundColor: color.hex ?? '#000' }}
                          />
                          <span className="text-small text-gray-700">{color.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePreview}
                  disabled={!card.message || !recipient.name}
                >
                  Vorschau
                </Button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <Card>
                <Card.Header>
                  <Card.Title>Vorschau</Card.Title>
                </Card.Header>
                <Card.Body className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-caption font-medium text-gray-500 mb-2">Empfaenger</p>
                      <div className="bg-gray-50 rounded-md p-4 text-small text-gray-700">
                        <p className="font-medium">{recipient.name}</p>
                        {recipient.company && <p>{recipient.company}</p>}
                        <p>{recipient.street}</p>
                        <p>
                          {recipient.zipCode} {recipient.city}
                        </p>
                      </div>
                    </div>

                    {hasSender && (
                      <div>
                        <p className="text-caption font-medium text-gray-500 mb-2">Absender</p>
                        <div className="bg-gray-50 rounded-md p-4 text-small text-gray-700">
                          <p className="font-medium">{sender.name}</p>
                          {sender.company && <p>{sender.company}</p>}
                          <p>{sender.street}</p>
                          <p>
                            {sender.zipCode} {sender.city}
                          </p>
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-caption font-medium text-gray-500 mb-2">Einstellungen</p>
                      <div className="bg-gray-50 rounded-md p-4 text-small text-gray-700 space-y-1">
                        <p>
                          <span className="text-gray-500">Typ:</span>{' '}
                          {card.type === 'letter'
                            ? 'Brief'
                            : card.type === 'postcard'
                              ? 'Postkarte'
                              : 'Grusskarte'}
                        </p>
                        {selectedFont && (
                          <p>
                            <span className="text-gray-500">Schrift:</span> {selectedFont.name}
                          </p>
                        )}
                        {selectedColor && (
                          <p className="flex items-center gap-1">
                            <span className="text-gray-500">Farbe:</span>
                            <span
                              className="inline-block w-3 h-3 rounded-full border border-gray-300"
                              style={{ backgroundColor: selectedColor.hex ?? '#000' }}
                            />
                            {selectedColor.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-caption font-medium text-gray-500 mb-2">Nachricht</p>
                    <div
                      className="bg-amber-50 border border-amber-200 rounded-md p-6 text-gray-800 whitespace-pre-wrap"
                      style={{
                        fontFamily: 'cursive',
                        fontSize: '1rem',
                        lineHeight: '1.8',
                        color: selectedColor?.hex ?? '#1a365d',
                      }}
                    >
                      {card.message}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setStep('compose')}>
                  Zurueck bearbeiten
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSend}
                  loading={sendMutation.isPending}
                  disabled={sendMutation.isPending}
                >
                  {sendMutation.isPending ? 'Wird gesendet…' : 'Brief versenden'}
                </Button>
              </div>
            </div>
          )}

          {step === 'sent' && orderResult && (
            <Card className="border-green-200 bg-green-50">
              <Card.Body className="space-y-3">
                <div className="flex items-center gap-2 text-green-800 font-semibold">
                  <CheckCircle2 className="w-5 h-5" />
                  Auftrag erstellt!
                </div>
                <div className="text-small text-green-700 space-y-1">
                  <p>
                    Auftrags-ID:{' '}
                    <span className="font-mono font-bold">{orderResult.orderId}</span>
                  </p>
                  <p>Status: <StatusBadge status={orderResult.status} /></p>
                  <p>
                    Preis:{' '}
                    {orderResult.price.toLocaleString('de', { minimumFractionDigits: 2 })}{' '}
                    {orderResult.currency}
                  </p>
                  {orderResult.estimatedDelivery && (
                    <p>
                      Voraussichtliche Zustellung:{' '}
                      {new Date(orderResult.estimatedDelivery).toLocaleDateString('de')}
                    </p>
                  )}
                </div>
                <Button variant="secondary" onClick={resetForm}>
                  Neuen Brief erstellen
                </Button>
              </Card.Body>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Gesendete Briefe
              </Card.Title>
              <IconButton
                icon={<RefreshCw className={historyQuery.isFetching ? 'animate-spin' : ''} />}
                aria-label="Aktualisieren"
                variant="ghost"
                size="sm"
                onClick={() => historyQuery.refetch()}
                disabled={historyQuery.isFetching}
              />
            </Card.Header>
            {historyQuery.isLoading ? (
              <LoadingState label="Auftraege werden geladen…" />
            ) : historyQuery.isError ? (
              <ErrorState
                title="Auftragshistorie nicht ladbar"
                description="PENSAKI nicht konfiguriert?"
                details={(historyQuery.error as Error)?.message}
              />
            ) : !historyQuery.data?.orders.length ? (
              <EmptyState title="Noch keine Auftraege vorhanden." />
            ) : (
              <>
                <DataTable<OrderHistoryItem>
                  data={historyQuery.data.orders}
                  columns={historyColumns}
                  getRowId={(row) => row.orderId}
                  className="rounded-none border-0 shadow-none"
                />
                {historyQuery.data.total > historyQuery.data.orders.length && (
                  <Card.Body>
                    <p className="text-caption text-gray-400">
                      {historyQuery.data.total} Auftraege insgesamt
                    </p>
                  </Card.Body>
                )}
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
